import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Recovery.css';
import Button from '../../components/Button';
import LoadingIndicator from '../../components/LoadingIndicator';
import {
  request2FARecovery,
  confirm2FARecovery,
} from '../../services/twoFactorAuthService';

const TwoFARecoveryPage: React.FC = () => {
  const [mode, setMode] = useState<'request' | 'backup'>('request');
  const [email, setEmail] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [done, setDone] = useState(false);

  // (1) メールでリセットリンクを送信
  const handleRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      await request2FARecovery(email);
      setMessage({
        type: 'success',
        text: 'リセットリンクをメールに送信しました。ご確認ください。',
      });
      setDone(true);
    } catch {
      setMessage({
        type: 'error',
        text: 'メール送信に失敗しました。メールアドレスをお確かめください。',
      });
    } finally {
      setLoading(false);
    }
  };

  // (2) バックアップコードで即時リセット
  const handleBackup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      await confirm2FARecovery({
        email,
        backup_code: backupCode,
      });
      setMessage({
        type: 'success',
        text: 'バックアップコードで2FAをリセットしました。再度ログインしてください。',
      });
      setDone(true);
    } catch (err: any) {
      const text =
        err.response?.data?.detail === 'invalid_backup_code'
          ? '無効なバックアップコードです。'
          : 'リセットに失敗しました。';
      setMessage({ type: 'error', text });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="recovery-page">
      <h1 className="recovery-title">2段階認証リセット</h1>

      <div className="mode-switch">
        <button
          className={mode === 'request' ? 'active' : ''}
          onClick={() => {
            setMode('request');
            setMessage(null);
            setDone(false);
          }}
        >
          メールでリンク送信
        </button>
        <button
          className={mode === 'backup' ? 'active' : ''}
          onClick={() => {
            setMode('backup');
            setMessage(null);
            setDone(false);
          }}
        >
          バックアップコード入力
        </button>
      </div>

      {message && (
        <div className={`notification ${message.type}`}>{message.text}</div>
      )}

      {!done && mode === 'request' && (
        <form className="recovery-form" onSubmit={handleRequest}>
          <label htmlFor="recovery-email">登録済みメールアドレス</label>
          <input
            id="recovery-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@example.com"
            required
            autoFocus
          />
          <Button type="submit" disabled={loading}>
            {loading ? <LoadingIndicator size={20} /> : 'リセットリンクを送信'}
          </Button>
        </form>
      )}

      {!done && mode === 'backup' && (
        <form className="recovery-form" onSubmit={handleBackup}>
          <label htmlFor="backup-email">登録済みのメールアドレス</label>
          <input
            id="backup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="メールアドレス"
          />
          <label htmlFor="backup-code">バックアップコードを入力</label>
          <input
            id="backup-code"
            type="text"
            value={backupCode}
            onChange={(e) => setBackupCode(e.target.value)}
            placeholder="例: GV988XU8"
            required
            autoFocus
          />
          <Button type="submit" disabled={loading}>
            {loading ? <LoadingIndicator size={20} /> : 'コードでリセット'}
          </Button>
        </form>
      )}

      {done && (
        <div className="links">
          <Link to="/login">ログインページへ戻る</Link>
        </div>
      )}
    </div>
  );
};

export default TwoFARecoveryPage;
